import {
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";

/* Shared once-only in-view observer (replaces framer-motion's useInView). */
function useInViewOnce(
  ref: RefObject<Element | null>,
  amount = 0.25,
): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: amount, rootMargin: "0px 0px -5% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, amount, inView]);
  return inView;
}


/* ───────────────────────── Eyebrow ───────────────────────── */
export function Eyebrow({
  children,
  className,
  withDot,
}: {
  children: ReactNode;
  className?: string;
  withDot?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2.5 font-display text-[12px] font-light uppercase tracking-[0.32em] text-tk-red",
        className,
      )}
    >
      {withDot && (
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inset-0 animate-ping rounded-full bg-tk-red/70" />
          <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-tk-red shadow-[0_0_12px_rgba(240,31,14,0.6)]" />
        </span>
      )}
      {children}
    </span>
  );
}

/* ───────────────── Editorial heading ─────────────────
 * Light Sora, tight tracking, with one Fraunces italic red accent word.
 * Pass `accent` (string or array of strings to italicize the matching token)
 * or use the `<i>` markup directly as a child.
 */
export function EditorialHeading({
  children,
  className,
  as: Tag = "h2",
}: {
  children: ReactNode;
  className?: string;
  as?: keyof React.JSX.IntrinsicElements;
}) {
  const Comp = Tag as keyof React.JSX.IntrinsicElements & string;
  return (
    <Comp
      className={cn(
        "font-display font-light leading-[0.98] tracking-[-0.035em] text-tk-cream",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

/* Convenience: italic-red accent inline */
export function Accent({ children }: { children: ReactNode }) {
  return <i className="editorial-accent not-italic-fix font-edit italic">{children}</i>;
}

/* Cursive "that" flourish — matches the logo's signature script */
export function Script({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("font-script text-tk-cream/95", className)}>{children}</span>
  );
}

/* ───────────────────────── Reveal ───────────────────────── */
export function Reveal({
  children,
  delay = 0,
  y = 30,
  className,
  style,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { delay?: number; y?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInViewOnce(ref, 0.2);
  return (
    <div
      ref={ref}
      className={cn("tk-reveal", inView && "tk-reveal-in", className)}
      style={{
        ...style,
        ["--tk-reveal-y" as string]: `${y}px`,
        transitionDelay: delay ? `${delay}s` : undefined,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}


/* ───────────────────────── Chip ───────────────────────── */
export function Chip({
  children,
  active,
  className,
}: {
  children: ReactNode;
  active?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-display text-[11px] uppercase tracking-[0.18em] transition-colors",
        active
          ? "border-tk-red bg-tk-red/15 text-tk-red"
          : "border-tk-line bg-tk-char text-tk-mute hover:border-tk-mute-2 hover:text-tk-cream",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ───────────────────────── Bubble ───────────────────────── */
export function Bubble({
  side = "user",
  delay = 0,
  children,
  className,
}: {
  side?: "user" | "bot";
  delay?: number;
  children: ReactNode;
  className?: string;
}) {
  const isUser = side === "user";
  return (
    <div
      style={{ animationDelay: delay ? `${delay}s` : undefined }}
      className={cn(
        "tk-pop max-w-[78%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-snug",
        isUser
          ? "ml-auto rounded-br-md bg-tk-cream text-tk-ink"
          : "mr-auto rounded-bl-md bg-tk-slate text-tk-cream/95",
        className,
      )}
    >
      {children}
    </div>

  );
}

/* ───────────────── Mascot float wrapper ─────────────────
 * Renders the mascot as either an image or an autoplaying video.
 * The video is wrapped in a circular cream halo so the studio-grey
 * background of the source clip blends into the cinematic dark canvas.
 */
export function MascotFloat({
  src,
  alt = "thatKorner mascot",
  className,
  poster,
}: {
  src: string;
  alt?: string;
  className?: string;
  poster?: string;
}) {
  const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(src);
  const hostRef = useRef<HTMLDivElement>(null);
  const nearViewport = useInViewOnce(hostRef, 0.01);

  return (
    <div
      ref={hostRef}
      className={cn(
        "tk-float pointer-events-none relative aspect-square select-none",
        className,
      )}
    >
      {/* warm cinematic halo behind the clip */}
      {isVideo && (
        <>
          <div
            aria-hidden
            className="absolute inset-[-45%] rounded-full"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(240,31,14,0.55) 0%, rgba(232,147,32,0.32) 35%, rgba(245,239,230,0.10) 60%, rgba(10,10,10,0) 80%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-[-12%] rounded-full opacity-80"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(240,31,14,0.35), rgba(232,147,32,0.25), rgba(240,31,14,0.35))",
            }}
          />

          <div
            aria-hidden
            className="absolute inset-0 rounded-full ring-2 ring-tk-red/40 shadow-[0_0_60px_rgba(240,31,14,0.55),0_0_120px_rgba(232,147,32,0.35)]"
          />
        </>
      )}

      {isVideo ? (
        <div className="relative h-full w-full overflow-hidden rounded-full bg-[#dadada] shadow-[0_50px_120px_-20px_rgba(240,31,14,0.55),0_30px_60px_-10px_rgba(0,0,0,0.6)]">
          {nearViewport ? (
            <video
              src={src}
              poster={poster}
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              aria-label={alt}
              className="h-full w-full object-cover [filter:saturate(1.15)_contrast(1.05)_brightness(1.05)] [transform:scale(1.08)]"
            >
              <track kind="captions" src="/mascot-captions.vtt" srcLang="en" label="English" default />
            </video>
          ) : (
            <img
              src={poster ?? src}
              alt={alt}
              draggable={false}
              className="h-full w-full object-cover [transform:scale(1.08)]"
            />
          )}
          {/* subtle inner vignette — keep mascot bright and visible */}
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              boxShadow:
                "inset 0 0 30px 4px rgba(10,10,10,0.25), inset 0 0 80px 10px rgba(10,10,10,0.12)",
            }}
          />
          {/* warm rim light */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 30% 25%, rgba(255,200,150,0.18) 0%, transparent 45%)",
            }}
          />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="h-full w-full object-contain drop-shadow-[0_30px_55px_rgba(0,0,0,0.55)]"
        />
      )}
    </div>
  );
}


/* ───────────────────── Phone mock ─────────────────────
 * A clean rounded device — ~28px radius, configurable height + tilt.
 * Use the red variant for the hero (full red screen + K-mark moment).
 */
export function PhoneMock({
  children,
  tilt = -3,
  hoverTilt = -1,
  variant = "screen",
  className,
}: {
  children: ReactNode;
  tilt?: number;
  hoverTilt?: number;
  variant?: "device" | "screen";
  className?: string;
}) {
  return (
    <div
      style={{ transform: `rotate(${tilt}deg)` }}
      className={cn(
        "group relative inline-block transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
        className,
      )}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = `rotate(${hoverTilt}deg)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = `rotate(${tilt}deg)`;
      }}
    >
      {variant === "device" ? (
        <div className="relative rounded-[44px] border border-tk-line bg-tk-slate p-2.5 shadow-[0_50px_120px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(245,239,230,0.04)_inset]">
          <div className="absolute left-1/2 top-2.5 z-20 h-1.5 w-24 -translate-x-1/2 rounded-full bg-tk-ink/80" />
          <div className="overflow-hidden rounded-[34px] bg-tk-ink">{children}</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[28px] border border-tk-red/30 bg-tk-red shadow-[0_60px_140px_-30px_rgba(240,31,14,0.45),0_0_0_1px_rgba(240,31,14,0.25)]">
          {children}
        </div>
      )}
    </div>
  );
}

/* ───────────────────── Animated counter ───────────────────── */
export function StatCounter({
  to,
  from = 0,
  duration = 1.6,
  decimals = 0,
  prefix,
  suffix,
  className,
}: {
  to: number;
  from?: number;
  duration?: number;
  decimals?: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInViewOnce(ref, 0.5);
  const [val, setVal] = useState(from);

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // cubic-out
    const tick = (now: number) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      setVal(from + (to - from) * ease(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, from, to, duration]);

  return (
    <span ref={ref} className={cn("tabular-nums", className)}>
      {prefix && <span className="editorial-accent mr-1 text-[0.7em]">{prefix}</span>}
      {val.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix && <span className="ml-0.5 text-tk-mute text-[0.45em] font-display tracking-wide">{suffix}</span>}
    </span>
  );
}

/* ───────────────────── Ticker (40s loop) ───────────────────── */
export function Ticker({ items }: { items: string[] }) {
  const row = (
    <div className="flex shrink-0 items-center gap-10 px-5">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className="flex items-center gap-10 whitespace-nowrap font-display text-[13px] font-light uppercase tracking-[0.32em] text-tk-cream/70"
        >
          {item}
          <span className="text-tk-red">✦</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative overflow-hidden border-y border-tk-line bg-tk-ink py-5">
      <div className="flex w-max animate-ticker">
        {row}
        {row}
      </div>
    </div>
  );
}

/* ───────────────────── Grain overlay ───────────────────── */
export function GrainOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] hidden opacity-[0.04] mix-blend-overlay md:block"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }}
    />
  );
}

/* ───────────────────── Section shell ───────────────────── */
export function Section({
  children,
  className,
  bordered = true,
  id,
}: {
  children: ReactNode;
  className?: string;
  bordered?: boolean;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        "relative px-gutter py-[clamp(80px,12vw,160px)]",
        bordered && "border-b border-tk-line",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-content">{children}</div>
    </section>
  );
}
